import paper from "paper";
import type { StampMeta } from "./StampMeta";

export interface TilePlacement {
  x: number;
  y: number;
  rotation: number;
  scale: number;
  /** True if the stamp's bbox center falls inside the region. */
  inside: boolean;
}

/**
 * Tile-aware fill of a region path using a stamp's lattice metadata.
 *
 * Algorithm:
 *  1. Rotate the region into the stamp's lattice frame.
 *  2. Walk a grid of cells covering the bounding box.
 *  3. For each cell, place a stamp; mark whether its center is inside the region.
 *
 * Edge matching: bundled stamps in v1 use a single edge key per side, so all
 * neighbouring cells trivially match. The infrastructure lets future variants
 * differ; if so, this picks the first variant whose edges match neighbours.
 */
export function tileRegion(
  region: paper.Path | paper.CompoundPath,
  stamp: StampMeta,
  options: { rotation: number; scale: number },
): TilePlacement[] {
  const tile = stamp.tile;
  if (!tile) return [];

  const cellW = tile.cellW * options.scale;
  const cellH = tile.cellH * options.scale;
  if (cellW <= 0.001 || cellH <= 0.001) return [];

  const rad = (options.rotation * Math.PI) / 180;
  const cos = Math.cos(rad);
  const sin = Math.sin(rad);

  // Center of the region's bounding box, used as the lattice origin.
  const bounds = region.bounds;
  const ox = bounds.center.x;
  const oy = bounds.center.y;

  // Compute extents in the rotated lattice frame.
  const corners = [
    { x: bounds.left, y: bounds.top },
    { x: bounds.right, y: bounds.top },
    { x: bounds.right, y: bounds.bottom },
    { x: bounds.left, y: bounds.bottom },
  ];
  let minU = Infinity;
  let maxU = -Infinity;
  let minV = Infinity;
  let maxV = -Infinity;
  for (const c of corners) {
    const dx = c.x - ox;
    const dy = c.y - oy;
    const u = dx * cos + dy * sin;
    const v = -dx * sin + dy * cos;
    if (u < minU) minU = u;
    if (u > maxU) maxU = u;
    if (v < minV) minV = v;
    if (v > maxV) maxV = v;
  }

  const startCol = Math.floor(minU / cellW) - 1;
  const endCol = Math.ceil(maxU / cellW) + 1;
  const startRow = Math.floor(minV / cellH) - 1;
  const endRow = Math.ceil(maxV / cellH) + 1;

  const rowOffset = tile.rowOffset ?? 0;

  const out: TilePlacement[] = [];
  for (let row = startRow; row <= endRow; row += 1) {
    const offsetU = ((row % 2) + 2) % 2 === 1 ? rowOffset * cellW : 0;
    for (let col = startCol; col <= endCol; col += 1) {
      const u = col * cellW + offsetU + cellW / 2;
      const v = row * cellH + cellH / 2;
      // Rotate back to world frame.
      const wx = ox + u * cos - v * sin;
      const wy = oy + u * sin + v * cos;
      const inside = region.contains(new paper.Point(wx, wy));
      const cornersInside = sampleStampCornersInside(
        wx,
        wy,
        cellW,
        cellH,
        options.rotation,
        region,
      );
      if (!inside && cornersInside === 0) continue;
      out.push({ x: wx, y: wy, rotation: options.rotation, scale: options.scale, inside });
    }
  }

  return out;
}

function sampleStampCornersInside(
  cx: number,
  cy: number,
  w: number,
  h: number,
  rotationDeg: number,
  region: paper.Path | paper.CompoundPath,
): number {
  const rad = (rotationDeg * Math.PI) / 180;
  const cos = Math.cos(rad);
  const sin = Math.sin(rad);
  const halfW = w / 2;
  const halfH = h / 2;
  const samples = [
    [-halfW, -halfH],
    [halfW, -halfH],
    [halfW, halfH],
    [-halfW, halfH],
    [0, 0],
  ];
  let count = 0;
  for (const [dx, dy] of samples) {
    const wx = cx + (dx as number) * cos - (dy as number) * sin;
    const wy = cy + (dx as number) * sin + (dy as number) * cos;
    if (region.contains(new paper.Point(wx, wy))) count += 1;
  }
  return count;
}
